import { buildCommunicationDomain } from './communicationDomain';
import { buildWhatHelpsDomain } from './whatHelpsDomain';
import { buildWatchForDomain } from './watchForDomain';
import { buildDailyLifeDomain } from './dailyLifeDomain';
import { buildSafetyDomain } from './safetyDomain';
import { buildHealthCareDomain } from './healthCareDomain';
import { buildGoalsDomain } from './goalsDomain';

const buildDomainStub = (id) => ({ id, status: 'stub' });

const getDomainInput = (input = {}) => {
  const child = input.child || {};
  const careContext = input.careContext === undefined
    ? child.careContext || {}
    : input.careContext;
  const ageGroup = input.ageGroup === undefined
    ? careContext?.ageGroup || ''
    : input.ageGroup;
  return {
    child,
    knowledgeFacts: input.knowledgeFacts || [],
    goals: input.goals === undefined ? child.supportProfile?.careGoals || [] : input.goals,
    careContext,
    ageGroup,
  };
};

const createContract = ({ communication, whatHelpsComfort, watchFor, dailyLifeRoutines, safety, healthCare, goals }) => ({
  person: buildDomainStub('DM_01'),
  communication,
  whatHelpsComfort,
  watchFor,
  dailyLifeRoutines,
  safety,
  healthCare,
  goals,
  protectedLogistics: buildDomainStub('DM_09'),
  protectedLegalAdmin: buildDomainStub('DM_10'),
});

const DEFAULT_DOMAIN_BUILDERS = Object.freeze({
  communication: buildCommunicationDomain,
  whatHelpsComfort: buildWhatHelpsDomain,
  watchFor: buildWatchForDomain,
  dailyLifeRoutines: buildDailyLifeDomain,
  safety: buildSafetyDomain,
  healthCare: buildHealthCareDomain,
  goals: buildGoalsDomain,
});

const EMPTY_DOMAIN_BUILDERS = Object.freeze({
  communication: () => buildCommunicationDomain(),
  whatHelpsComfort: () => buildWhatHelpsDomain(),
  watchFor: () => buildWatchForDomain(),
  dailyLifeRoutines: () => buildDailyLifeDomain(),
  safety: () => buildSafetyDomain(),
  healthCare: () => buildHealthCareDomain(),
  goals: () => buildGoalsDomain(),
});

export const buildProfileContract = (input = {}) => {
  const domainInput = getDomainInput(input);
  return createContract({
    communication: buildCommunicationDomain(domainInput),
    whatHelpsComfort: buildWhatHelpsDomain(domainInput),
    watchFor: buildWatchForDomain(domainInput),
    dailyLifeRoutines: buildDailyLifeDomain(domainInput),
    safety: buildSafetyDomain(domainInput),
    healthCare: buildHealthCareDomain(domainInput),
    goals: buildGoalsDomain(domainInput),
  });
};

export const buildProfileContractSafely = (input, builder = buildProfileContract) => {
  if (builder && typeof builder === 'object') {
    const domainInput = getDomainInput(input);
    const builders = { ...DEFAULT_DOMAIN_BUILDERS, ...builder };
    const errors = [];
    const chapters = Object.fromEntries(Object.keys(DEFAULT_DOMAIN_BUILDERS).map((key) => {
      try {
        return [key, builders[key](domainInput)];
      } catch (error) {
        errors.push(error);
        return [key, EMPTY_DOMAIN_BUILDERS[key]()];
      }
    }));
    return { contract: createContract(chapters), error: errors[0] || null };
  }

  if (builder === buildProfileContract) {
    return buildProfileContractSafely(input, DEFAULT_DOMAIN_BUILDERS);
  }

  try {
    return { contract: builder(input), error: null };
  } catch (error) {
    return {
      contract: createContract({
        communication: buildCommunicationDomain(),
        whatHelpsComfort: buildWhatHelpsDomain(),
        watchFor: buildWatchForDomain(),
        dailyLifeRoutines: buildDailyLifeDomain(),
        safety: buildSafetyDomain(),
        healthCare: buildHealthCareDomain(),
        goals: buildGoalsDomain(),
      }),
      error,
    };
  }
};
